import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="radius-search"
export default class extends Controller {
  static values = { selected: String, original: String };
  static targets = ['button', 'popup', 'inputs', 'selectLocation'];
  static outlets = ['gmaps-radius'];

  connect() {
    this.buttonTarget.setAttribute('data-active', this.selectedValue !== '');
  }

  changeSelection(event) {
    if (event.detail && 'radius' in event.detail) {
      this.selectedValue = event.detail.radius || '';

      // We're getting called by code. So we'd like to sync out input state to
      // our value, and update our appearance. Furthermore we'd like to update
      // our "original" value, so a later click on the submit button doesn't
      // actually submit -- that's unnecessary, our outlet (which is where the
      // event that's triggering us came from) handled the data fetch itself.
      this.popupTarget.querySelector(
        `input[value="${this.selectedValue}"]`,
      ).checked = true;

      this.originalValue = this.selectedValue;

      this.buttonTarget.setAttribute('data-active', this.selectedValue !== '');
    } else {
      // We're getting called because the user clicked an input.
      this.selectedValue = event.target.value;
    }
  }

  maybeSubmit() {
    this.buttonTarget.setAttribute('data-active', this.selectedValue !== '');

    if (this.originalValue !== this.selectedValue) {
      this.submitOrNotifyOutlet();
    }

    // We're requesting a turbo frame that doesn't include us, probably, so we won't get rerendered.
    // So we need to update our values ourselves.
    this.originalValue = this.selectedValue;
  }

  reset() {
    this.popupTarget.querySelector('input[type=radio][value=""]').checked =
      true;

    this.selectedValue = '';

    if (this.originalValue !== '') {
      this.submitOrNotifyOutlet();
    }

    // We're requesting a turbo frame that doesn't include us, probably, so we won't get rerendered.
    // So we need to update our values ourselves.
    this.originalValue = '';
    this.buttonTarget.setAttribute('data-active', false);
  }

  submitOrNotifyOutlet() {
    if (!this.hasGmapsRadiusOutlet || !this.gmapsRadiusOutlet.hasMapTarget) {
      // We're probably in list mode, or maybe the user didn't agree to Google maps. Trigger a
      // submit, which will rerender the whole _search_results Turbo frame.

      // Wipe the map bounds -- we don't necessarily want the backend to search within the
      // *current* map bounds.
      const mapBoundsInput = document.querySelector('input[name=map_bounds]');
      if (mapBoundsInput) {
        mapBoundsInput.value = '';
      }

      this.popupTarget
        .closest('form')
        .querySelector('button[type=submit]')
        .click();
    } else {
      // The user agreed to Google Maps & a map is displayed. Sync the state of the controller there
      // to ours; as a consequence, some data will be dynamically fetched (without so much as a
      // Turbo frame reload) and the map will pan. This is better UI than a submit.
      this.gmapsRadiusOutlet.radiusValue = this.selectedValue;
    }
  }

  // It shouldn't be possible to set a radius when searching for location-independent projects,
  // so we disable our button. We don't notify the outlet: changing the "location-independent"
  // checkbox refetches the whole Turbo frame, and the outlet will be displayed or not as the
  // backend deems fit.
  onLocationIndependentOn() {
    this.buttonTarget.disabled = true;
  }

  // Enable radius searches. See onLocationIndependentOn.
  onLocationIndependentOff() {
    this.buttonTarget.disabled = false;
  }

  // Here, too, we don't need to notify our outlet because submitting a new location search
  // refetches the whole Turbo frame.
  handleFormdata(event) {
    const formData = event.formData;

    const haveLat = formData.get('longitude');
    const haveLng = formData.get('latitude');
    const onlyLocationIndependent = formData.get('only_location_independent');

    const radiusSearchPossible = !!(
      haveLat &&
      haveLng &&
      !onlyLocationIndependent
    );

    if (!radiusSearchPossible) {
      this.selectedValue = this.originalValue = '';
      this.buttonTarget.setAttribute('data-active', false);
      this.inputsTarget.querySelector('input[value=""]').checked = true;
      formData.delete('radius');
    }

    if (radiusSearchPossible) {
      this.selectLocationTarget.classList.add('display-none');
      this.inputsTarget.classList.remove('display-none');
    } else {
      this.selectLocationTarget.classList.remove('display-none');
      this.inputsTarget.classList.add('display-none');
    }
  }
}
